import React, { useCallback } from "react";
import moment, { Moment } from "moment";
import { Button } from "@myhealth-raccoon/components";
import { useAsyncData } from "@myhealth-raccoon/services";

import { AvailabilityCalendar } from "./AvailablilityCalendar";
import { AppointmentType, AutomedClinicAvailabilityTimeModel, AutomedDoctorAvailabilityTimeModel, availabilityService, FirstAvailableModel } from "src/services/availability";
import { toAutomedClinicAvailabilityTimeModel } from "src/utils/mapAutomedTimeTypes";

export type AvailabilityProps = {
    appointmentType: AppointmentType;
    clinicId: number;
    selected?: Moment;
    disabled?: boolean;
    firstAvailableDate?: string;
    firstAvailableIsLoading?: boolean;
    errorElement?: React.ReactNode;
    futureApptWeeks?: number;
} & ({
    onSelectTimeSlot(appointment: { date: Date, doctorId: number, clinicId: number, appointmentTypeId: string, timeSlot: AutomedDoctorAvailabilityTimeModel }): void;
    doctorId: string | number;
} | {
    onSelectTimeSlot(appointment: { date: Date, doctorId: number[], clinicId: number, appointmentTypeId: string, timeSlots: AutomedClinicAvailabilityTimeModel[] }): void;
    doctorId?: undefined;
})

export function Availability({ doctorId, appointmentType, clinicId, onSelectTimeSlot, disabled, errorElement, futureApptWeeks }: AvailabilityProps) {

    const { data: firstAvailableModel, isLoading } = useAsyncData({
        service: availabilityService,
        method: availabilityService.GetFirstAvailable,
        selector: useCallback((slots: FirstAvailableModel[]) => {
            const sorted = slots.filter(x => x.firstAvailable).sort(({ firstAvailable: { time: ta, date: da } }, { firstAvailable: { time: tb, date: db } }) => moment(`${da} ${ta}`).isBefore(moment(`${db} ${tb}`)) ? -1 : 1);
            if (!slots.length) {
                return null;
            }
            const firstAvailables = sorted.filter(({ firstAvailable: { date, time } }) => date === sorted[0].firstAvailable.date && time === sorted[0].firstAvailable.time)
            return {
                firstAvailable: firstAvailables[0].firstAvailable,
                users: firstAvailables.map(s => s.user)
            }
        }, []),
        suppressToast: true
    },
        clinicId,
        appointmentType.appointmentTypeId,
        doctorId ?? null,
    );

    return <div>
        <AvailabilityCalendar
            appointmentType={appointmentType}
            clinicId={clinicId}
            doctorId={doctorId}
            disabled={disabled}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onSelectTimeSlot={onSelectTimeSlot as any}
            firstAvailableDate={firstAvailableModel?.firstAvailable?.date}
            firstAvailableIsLoading={isLoading}
            errorElement={errorElement}
            futureApptWeeks={futureApptWeeks}
        />

        {firstAvailableModel && firstAvailableModel.firstAvailable && !errorElement && <>
            <div className="flex px-8 gap-2 items-center font-medium text-gray-600 before:content-[''] before:flex-1 before:h-px before:bg-gray-200 after:content-[''] after:flex-1 after:h-px after:bg-gray-200">
                OR
            </div>

            <div className="px-4 mt-4">
                <Button className="w-full"
                    onClick={() => onSelectTimeSlot({
                        date: moment(`${firstAvailableModel.firstAvailable.date} ${firstAvailableModel.firstAvailable.time}`).toDate(),
                        doctorId: (doctorId !== undefined ? Number(doctorId) : firstAvailableModel.users.map(({ userId }) => Number(userId))) as number & number[],
                        appointmentTypeId: appointmentType.appointmentTypeId,
                        clinicId,
                        timeSlot: {
                            ...firstAvailableModel.firstAvailable
                        },
                        timeSlots: [
                            toAutomedClinicAvailabilityTimeModel(firstAvailableModel.firstAvailable)
                        ]
                    })}
                    disabled={isLoading || disabled}>
                    Next available:
                    {' '}
                    {moment(`${firstAvailableModel.firstAvailable.date} ${firstAvailableModel.firstAvailable.time}`).calendar(null, {
                        sameElse: 'l [at] LT'
                    })}
                    {firstAvailableModel.users.length === 1 &&
                        ` with ${firstAvailableModel.users[0].displayName}`}
                </Button>
            </div>
        </>
        }
    </div>
}